import React from "react";
import { Button, IconButton } from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useEnvironment } from "../../../graphql/environment";
import { fetchQuery } from "relay-hooks";
import * as Sentry from "@sentry/browser";
import { FeatureTestsSentryQuery } from "./__generated__/FeatureTestsSentryQuery.graphql";
import styled from "styled-components/macro";
import { isAndroid, isApp, isIos } from "tools/device";
import { useSubscribe, useSubscriptions } from "root/profile/subscription/subscriptionHooks";
import { useGetState, useSetState } from "components/ReduxProvider";
import { SALE } from "sharedJs__generated/constants";
import { HeaderLayout } from "components/header/Header";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import RefreshIcon from "@mui/icons-material/Refresh";
import CampaignIcon from "@mui/icons-material/Campaign";
import ShareIcon from "@mui/icons-material/Share";
import InfoIcon from "@mui/icons-material/Info";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import SettingsIcon from "@mui/icons-material/Settings";
import StarIcon from "@mui/icons-material/Star";
import { initAndLoadInterstitial } from "components/ads/AdsLoader";
import { windowReload } from "tools/windowReload";
import launchRating from "tools/launchRatings";
import { logEvent, logPurchase } from "tools/analyticEvents";
import { TTS_TEST } from "../AdminRouter";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import { useNavigate } from "react-router-dom";
import VibrationIcon from "@mui/icons-material/Vibration";
import { useMedia } from "react-use";
import { setABTestOverride } from "queries/users/useABTest";

const query = graphql`
  query FeatureTestsSentryQuery($internalUndefined: Boolean, $throwError: Boolean) {
    sentryTest(internalUndefined: $internalUndefined, throwError: $throwError)
  }
`;

const Check = styled.div<{ ok: boolean }>`
  color: ${({ ok }) => (ok ? "green" : "red")};
  button {
    padding: 1px 2px;
  }
`;

const ABTestSection = styled.div`
  margin: 20px 0;
  padding: 10px;
`;

const FeatureTests: React.FC<{}> = () => {
  const testVal = "someString";
  const navigate = useNavigate();
  const environment = useEnvironment();
  const { store } = useSubscriptions();
  const subscribe = useSubscribe();
  const isDarkMode = useMedia("(prefers-color-scheme: dark)");
  const lastSeen = useGetState("saleMainDialogLastSeen");
  const setLastSeen = useSetState("saleMainDialogLastSeen");
  const setCloseSale = useSetState("closeSale");

  const qr = (vars) => fetchQuery<FeatureTestsSentryQuery>(environment, query, vars);

  return (
    <HeaderLayout title="Feature tests" style={{ textAlign: "center" }}>
      <h1>AB Test Controls</h1>
      <ABTestSection>
        <div>
          <div style={{ marginBottom: 12 }}>
            <strong>Override AB Tests</strong>
            <br />
            <small style={{ color: "#666" }}>
              Force all AB tests to return specific value. Persists in localStorage. Remove override by clicking the
              indicator. Hold the indicator to toggle the value.
            </small>
          </div>
          <Button variant="text" size="small" onClick={() => setABTestOverride(true)}>
            Enable All
          </Button>
          <Button variant="text" size="small" onClick={() => setABTestOverride(false)}>
            Disable All
          </Button>
        </div>
      </ABTestSection>
      <h1>Sale dialog controls</h1>
      <div style={{ margin: "12px 0" }}>
        <label htmlFor="saleSelect" style={{ marginRight: 8 }}>
          Set last seen sale:
        </label>
        <select
          id="saleSelect"
          value={lastSeen ?? ""}
          onChange={(e) => setLastSeen(e.target.value as unknown as SALE)}
          style={{ padding: "6px 10px" }}
        >
          <option value="">(none)</option>
          {Object.entries(SALE).map(([key, value]) => (
            <option key={value} value={value}>
              {key}
            </option>
          ))}
        </select>
      </div>
      <div style={{ margin: "12px 0" }}>
        <Button variant="text" size="small" onClick={() => setCloseSale(false)}>
          Reset small dialog in sub screen
        </Button>
      </div>
      <h1>Plugins checks:</h1> {/* @ts-ignore*/}
      {!isApp() ? (
        <>Cordova N/A</>
      ) : (
        <>
          <Check ok={!!window.cordova?.plugins?.firebase?.messaging}>
            cordova-plugin-firebase-messaging
            <IconButton
              onClick={() => {
                window.cordova?.plugins?.firebase?.messaging
                  ?.requestPermission()
                  .then(() => {
                    alert("notifications permission is granted");
                  })
                  .catch((e) => alert("error: " + JSON.stringify(e)));
              }}
            >
              <InfoIcon />
            </IconButton>
          </Check>
          <Check ok={!!store}>
            cordova-plugin-purchase{" "}
            <IconButton onClick={() => subscribe()}>
              <ShoppingCartIcon />
            </IconButton>
          </Check>
          <Check ok={!!window.admob}>
            admob-plus-cordova
            <IconButton
              disabled={!window.admob}
              onClick={async () => {
                const interstitial = await initAndLoadInterstitial();
                setTimeout(() => {
                  interstitial.show();
                }, 2000);
              }}
            >
              <CampaignIcon />
            </IconButton>
          </Check>
          <Check ok={!!window.consent}>
            admob-plugin-consent
            <IconButton
              disabled={!window.consent}
              onClick={async () => {
                try {
                  const canRequest = await window.consent.canRequestAds();
                  alert(canRequest ? "can request ads" : "can't request ads");
                } catch (e: any) {
                  alert("error: " + e.message);
                }
              }}
            >
              <InfoIcon />
            </IconButton>
            <IconButton
              disabled={!window.consent}
              onClick={() => {
                window.consent
                  .reset()
                  .then(windowReload) // reload to show consent form
                  .catch((e) => alert("error: " + e.message));
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Check>

          <Check ok={!!navigator.share}>
            cordova-plugin-share
            <IconButton
              onClick={() => {
                // @ts-ignore
                navigator.share("test", undefined, "text/plain");
              }}
            >
              <ShareIcon />
            </IconButton>
          </Check>

          <Check ok={typeof (window as any).handleOpenURL === "function"}>cordova-plugin-customurlscheme</Check>

          <Check ok={!!window.universalLinks?.subscribe}>cordova-universal-links-plugin</Check>

          <Check ok={!!window.LaunchReview}>
            cordova-launch-review
            <IconButton onClick={() => launchRating()}>
              <StarIcon />
            </IconButton>
          </Check>

          <Check ok={!!window.cordova?.plugins?.settings}>
            cordova-open-native-settings
            <IconButton
              onClick={() =>
                window.cordova?.plugins?.settings?.open(
                  "notification_id",
                  function () {
                    console.log("opened");
                  },
                  function () {
                    alert("error opening settings");
                  }
                )
              }
            >
              <SettingsIcon />
            </IconButton>
          </Check>

          <Check ok={!!window.cordova?.plugins?.firebase?.analytics}>
            cordova-plugin-firebase-analytics
            <IconButton onClick={() => window.cordova?.plugins?.firebase?.analytics?.logEvent("test")}>
              <ForwardToInboxIcon />
            </IconButton>
          </Check>

          <Check ok={!!(window as any).facebookConnectPlugin}>
            cordova-plugin-facebook-connect
            <IconButton onClick={() => (window as any).facebookConnectPlugin?.logEvent("test")}>
              <ForwardToInboxIcon />
            </IconButton>
          </Check>

          <Check ok={!!window.cordova?.plugins?.SingularCordovaSdk}>
            singular_cordova_sdk
            <IconButton
              onClick={() =>
                window.cordova?.plugins?.SingularCordovaSdk?.eventWithArgs("TEST_EVENT", { test: "test_content" })
              }
            >
              <ForwardToInboxIcon />
            </IconButton>
          </Check>

          <Check ok={!!window.plugin?.volume}>
            cordova-volume
            <IconButton onClick={() => window.plugin?.volume?.getVolume((volume) => alert("volume: " + volume))}>
              <InfoIcon />
            </IconButton>
          </Check>

          <Check ok={!!navigator.vibrate}>
            cordova-plugin-vibration
            <IconButton onClick={() => navigator.vibrate(1000)}>
              <VibrationIcon />
            </IconButton>
          </Check>

          <Check ok={!!window.audioinput}>cordova-plugin-audioinput</Check>

          <Check ok={!!window.cordova?.InAppBrowser}>cordova-plugin-inappbrowser</Check>

          {isAndroid() && <Check ok={!!window.ClarityPlugin}>cordova-clarity</Check>}

          {isIos() && <Check ok={!!(window as any).Adjust}>com.adjust.sdk</Check>}
        </>
      )}
      <h1>Events tests:</h1> {/* @ts-ignore*/}
      <Button
        onClick={() => {
          window?.fbq?.("track", "test");
          console.log(window?.fbq ? "sent" : "fbq not found");
        }}
      >
        Meta pixel - Send test event
      </Button>
      <Button
        onClick={() => {
          console.log(window?.fbq ? "sent" : "window.fbq not set");
          window?.fbq?.("track", "Purchase", { currency: "USD", value: 1.0 });
        }}
      >
        Meta pixel - Purchase event
      </Button>
      <Button onClick={() => logEvent("TEST15989")}>General Event</Button>
      <Button onClick={() => logPurchase({ price: 1.0, currency: "CZK" })}>General Purchase event</Button>
      <h1>Sentry tests:</h1> {/* @ts-ignore*/}
      <Button onClick={() => testVal.a()}>React - call function on undefined</Button>
      <Button onClick={() => Sentry.captureMessage("TEST Sentry.captureMessage")}>React - captureMessage</Button>
      <Button onClick={() => qr({ internalUndefined: true }).subscribe({})}>Express - fn on undefined</Button>
      <Button onClick={() => qr({ throwError: true }).subscribe({})}>Express - unknown error</Button>
      <div>window.appversion: {window.appversion}</div>
      <div>device theme: {isDarkMode ? "dark" : "light"}</div>
      <h1>TTS:</h1>
      <Button onClick={() => navigate(TTS_TEST.url())}>
        <RecordVoiceOverIcon /> Test tts on different platforms
      </Button>
    </HeaderLayout>
  );
};

export default FeatureTests;
